import { Constructor } from '@angular/cdk/table';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IDigitalPublicationSection } from 'src/app/core/dtos/interfaces/i-digital-publication-section';
import { compareArrays } from '../utils/array-utils';
import { deepEqual } from '../utils/deep-equal';

type WithSectionsFormSectionType = {
  _initSections(sectionFormArray: IDigitalPublicationSection[]);
  _initSectionsValueChanges(
    getPreviousSlidesValue: () => IDigitalPublicationSection[] | null
  );
  _initSectionValidation();
  _buildSections();
};

type WithSectionsFormSectionRequirementsBase = {
  form: FormGroup;
  FORM_PATHS: {
    SECTIONS: () => string;
  };
  destroy$: Subject<boolean>;
};

export function WithDigitalPublicationSections<
  TBase extends Constructor<WithSectionsFormSectionRequirementsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithSectionsFormSectionType;
  prototype: WithSectionsFormSectionType;
} & TBase {
  return class extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    _initSections(sectionFormArray: IDigitalPublicationSection[]): void {
      let sections = this.form.get(this.FORM_PATHS.SECTIONS()) as FormArray<
        FormControl<IDigitalPublicationSection>
      >;

      sectionFormArray.forEach((section) => {
        sections.push(new FormControl(section));
      });
    }

    _initSectionsValueChanges(
      getPreviousSectionsValue: () => IDigitalPublicationSection[] | null
    ): void {
      this.form
        .get(this.FORM_PATHS.SECTIONS())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (compareArrays(newValue, getPreviousSectionsValue())) {
              this.form.get(this.FORM_PATHS.SECTIONS()).markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.SECTIONS()).markAsDirty();
              return;
            }
            this.form
              .get(this.FORM_PATHS.SECTIONS())
              .value.forEach((section, index) => {
                if (deepEqual(section, getPreviousSectionsValue()[index])) {
                  this.form.get(this.FORM_PATHS.SECTIONS()).markAsPristine();
                } else {
                  this.form.get(this.FORM_PATHS.SECTIONS()).markAsDirty();
                  return;
                }
              });
          },
        });
    }

    _buildSections(): IDigitalPublicationSection[] {
      return this.form.get(this.FORM_PATHS.SECTIONS()).value;
    }

    _initSectionValidation(): ValidatorFn {
      return (sections: FormArray): ValidationErrors | null => {
        let duplicationErrors: ValidationErrors = { duplicate: true };
        let requiredErrors: ValidationErrors = { required: true };
        let hasDuplicationErrors = false;
        let hasRequiredErrors = false;

        sections.controls.forEach((i) => {
          for (let i = 0; i < sections.controls.length; i++) {
            const section = sections.controls[i];
            section.setErrors(null);
            if (!section.value.name) {
              hasRequiredErrors = true;
            }
            if (!section.value.path) {
              section.setErrors(requiredErrors);
              hasRequiredErrors = true;
            }
            sections.controls.forEach((i) => {
              if (
                section.value.id !== i.value.id &&
                section.value.path === i.value.path
              ) {
                section.setErrors(duplicationErrors);
                i.setErrors(duplicationErrors);
                hasDuplicationErrors = true;
              }
            });
          }
        });

        return hasRequiredErrors
          ? requiredErrors
          : hasDuplicationErrors
          ? duplicationErrors
          : null;
      };
    }
  };
}
